import { useState, type ReactNode } from 'react'

import { EmptyState, Skeleton } from '@/components/ui/Feedback'
import { StatStrip, type StatSegment } from '@/components/ui/StatStrip'
import { ApplicationStatusPill, STATUS_META } from '@/components/ui/StatusPill'
import { cn, relativeTime } from '@/lib/utils'
import type { Application, ApplicationStats, ApplicationStatus } from '@/types/api'

// ---------------------------------------------------------------------------
// Status words and tones. The pill itself comes from StatusPill; these two
// maps exist for callers that need the plain word or tone (the review page).
// ---------------------------------------------------------------------------
export const STATUS_TONE: Record<ApplicationStatus, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  submitted: 'success',
  in_flight: 'info',
  needs_you: 'warning',
  failed: 'danger',
  skipped: 'neutral',
  draft: 'neutral',
  queued: 'info',
  job_expired: 'neutral',
}

export const STATUS_LABEL: Record<ApplicationStatus, string> = Object.fromEntries(
  (Object.keys(STATUS_META) as ApplicationStatus[]).map((status) => [status, STATUS_META[status].label]),
) as Record<ApplicationStatus, string>

/** The status, or "Ready to submit" for a waiting application whose details are all filled. */
export function StatusBadge({ application, size }: { application: Application; size?: 'sm' | 'md' }) {
  return (
    <ApplicationStatusPill
      status={application.status}
      readyToSubmit={application.readyToSubmit}
      needsReview={application.needsReview}
      retried={Boolean(application.retriedAt)}
      waitingForYou={application.waitingForYou ?? null}
      size={size}
    />
  )
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------
interface Props {
  applications: Application[]
  loading?: boolean
  onOpen: (application: Application) => void
  /** Shown when the list is empty; defaults to a generic "nothing yet" message. */
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: ReactNode
}

export function ApplicationsTable({
  applications,
  loading,
  onOpen,
  emptyTitle = 'No applications yet',
  emptyDescription = 'Applications appear here as soon as you apply to a job from your matches.',
  emptyAction,
}: Props) {
  if (loading) return <ListSkeleton />

  if (applications.length === 0) {
    return <EmptyState icon={<InboxIcon />} title={emptyTitle} description={emptyDescription} action={emptyAction} />
  }

  return (
    <>
      {/* Phones: one tap target per row, nothing clips off the side. */}
      <ul className="divide-y divide-line md:hidden">
        {applications.map((application) => (
          <li key={application.id}>
            <PhoneRow application={application} onOpen={() => onOpen(application)} />
          </li>
        ))}
      </ul>

      {/* md+: a dense table. */}
      <div className="hidden md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-line bg-surface-muted/50 text-left">
              <Th className="px-5">Company</Th>
              <Th>Apply via</Th>
              <Th className="hidden lg:table-cell">Resume</Th>
              <Th>Status</Th>
              <Th className="px-5 text-right">Applied</Th>
            </tr>
          </thead>
          <tbody>
            {applications.map((application) => (
              <DesktopRow key={application.id} application={application} onOpen={() => onOpen(application)} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th scope="col" className={cn('label-eyebrow px-4 py-2.5 font-medium', className)}>
      {children}
    </th>
  )
}

function PhoneRow({ application, onOpen }: { application: Application; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-[64px] w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-surface-muted"
    >
      <CompanyAvatar name={application.job.companyName} logoUrl={application.job.company?.logoUrl} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14.5px] font-semibold leading-snug text-ink">{application.job.companyName}</span>
        <span className="block truncate text-[13px] text-ink-muted">{application.job.title}</span>
        <span className="mt-1 block truncate text-[12px] text-ink-faint">
          {applyVia(application)} · {resumeLabel(application)} resume ·{' '}
          <span className="num">{appliedAgo(application)}</span>
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1">
        <StatusBadge application={application} size="sm" />
        <Chevron />
      </span>
    </button>
  )
}

function DesktopRow({ application, onOpen }: { application: Application; onOpen: () => void }) {
  return (
    <tr
      onClick={onOpen}
      className="group cursor-pointer border-b border-line transition-colors last:border-0 hover:bg-surface-muted"
    >
      <td className="max-w-0 px-5 py-2.5 lg:w-[40%]">
        <div className="flex items-center gap-3">
          <CompanyAvatar name={application.job.companyName} logoUrl={application.job.company?.logoUrl} />
          <div className="min-w-0">
            <p className="truncate text-[14px] font-medium leading-snug text-ink">{application.job.companyName}</p>
            <p className="truncate text-[12.5px] text-ink-muted">{application.job.title}</p>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-[13px] text-ink-soft">{applyVia(application)}</td>
      <td className="hidden whitespace-nowrap px-4 py-2.5 text-[13px] text-ink-soft lg:table-cell">
        {resumeLabel(application)}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5">
        <StatusBadge application={application} />
      </td>
      <td className="num whitespace-nowrap px-5 py-2.5 text-right text-[12.5px] text-ink-muted">
        <span className="inline-flex items-center gap-2">
          {appliedAgo(application)}
          <Chevron className="opacity-0 transition-opacity group-hover:opacity-100" />
        </span>
      </td>
    </tr>
  )
}

function ListSkeleton() {
  return (
    <ul aria-busy="true" aria-label="Loading applications" className="divide-y divide-line">
      {Array.from({ length: 5 }).map((_, index) => (
        <li key={index} className="flex items-center gap-3 px-4 py-3.5 md:px-5">
          <Skeleton className="h-9 w-9 shrink-0 rounded-[9px]" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3.5 w-40 max-w-[60%]" />
            <Skeleton className="h-3 w-56 max-w-[80%]" />
          </div>
          <Skeleton className="h-5 w-20 rounded-pill" />
        </li>
      ))}
    </ul>
  )
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg className={cn('h-4 w-4 shrink-0 text-ink-faint', className)} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m6 4 4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/** When it was sent; never when it was created — a retry sets a new one, and until then it reads "Not sent". */
function appliedAgo(application: Application): string {
  return application.submittedAt ? relativeTime(application.submittedAt) : 'Not sent'
}

function resumeLabel(application: Application): string {
  return application.resumeVariant === 'optimized'
    ? 'Tailored'
    : application.resumeVariant === 'skipped'
      ? 'Skipped'
      : 'Default'
}

/** "Naukri", or "Company site (Workday) · found on Naukri" when the portal only pointed at the employer. */
function applyVia(application: Application): string {
  const via = application.appliedVia
  if (via) {
    const elsewhere = via.channel === 'company_site' && via.foundOn.key !== 'direct'
    return elsewhere ? `${via.label} · found on ${via.foundOn.label}` : via.label
  }
  if (application.applyMode === 'company_portal') return 'Company site'
  if (application.applyMode === 'easy_apply') return 'Naukri quick apply'
  if (application.job.source === 'naukri') return 'Naukri'
  return application.job.board?.name ?? 'Company site'
}

function InboxIcon() {
  return (
    <span className="grid h-11 w-11 place-items-center rounded-full bg-surface-muted text-ink-muted">
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M3 11.5 5 4.5h10l2 7m-14 0V15a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 17 15v-3.5m-14 0h4l1 2h4l1-2h4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Pager: phones get Prev / "x–y of n" / Next on one line; page numbers and
// the rows-per-page select appear from md.
// ---------------------------------------------------------------------------
interface PagerProps {
  page: number
  pageSize: number
  totalItems: number
  onPage: (page: number) => void
  onPageSize: (size: number) => void
}

export function Pager({ page, pageSize, totalItems, onPage, onPageSize }: PagerProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const first = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, totalItems)
  const pages = Array.from(new Set([1, page - 1, page, page + 1, totalPages]))
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b)

  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-3 py-2 text-[13px] text-ink-muted sm:px-4">
      <label className="hidden items-center gap-2 md:flex">
        Rows per page
        <select
          value={pageSize}
          onChange={(event) => onPageSize(Number(event.target.value))}
          className="h-8 rounded-[8px] border border-line bg-surface px-2 text-ink"
        >
          {[12, 25, 50].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      <PageStep label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)} className="md:hidden">
        ‹
      </PageStep>
      <span className="num text-ink-soft">
        {first}–{last} <span className="text-ink-muted">of</span> {totalItems}
      </span>
      <PageStep label="Next page" disabled={page >= totalPages} onClick={() => onPage(page + 1)} className="md:hidden">
        ›
      </PageStep>

      <nav aria-label="Pages" className="hidden items-center gap-1 md:flex">
        <PageStep label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          ‹
        </PageStep>
        {pages.map((p, index) => (
          <span key={p} className="flex items-center gap-1">
            {index > 0 && p - (pages[index - 1] ?? p) > 1 && <span className="px-1">…</span>}
            <button
              type="button"
              onClick={() => onPage(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                'num grid h-8 min-w-8 place-items-center rounded-[8px] px-2 text-[13px] transition-colors',
                p === page ? 'bg-ink text-oncolor' : 'text-ink hover:bg-surface-muted',
              )}
            >
              {p}
            </button>
          </span>
        ))}
        <PageStep label="Next page" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
          ›
        </PageStep>
      </nav>
    </div>
  )
}

function PageStep({
  label,
  disabled,
  onClick,
  className,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'grid h-10 w-10 place-items-center rounded-[8px] border border-line text-[15px] text-ink transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-ink-faint md:h-8 md:w-8 md:text-[13px]',
        className,
      )}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Company avatar: the logo on a surface tile, or two letters on a stable tint.
// ---------------------------------------------------------------------------
const AVATAR_TINTS = [
  'bg-info/12 text-info',
  'bg-brand-soft text-brand',
  'bg-warning/14 text-warning',
  'bg-danger/10 text-danger',
  'bg-accent-soft text-ink-soft',
]

export function CompanyAvatar({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  const [broken, setBroken] = useState(false)
  if (logoUrl && !broken) {
    return (
      <img
        src={logoUrl}
        alt=""
        onError={() => setBroken(true)}
        className="h-9 w-9 shrink-0 rounded-[9px] border border-line bg-surface object-contain p-0.5"
      />
    )
  }
  const index = Math.abs([...name].reduce((sum, ch) => sum + ch.charCodeAt(0), 0)) % AVATAR_TINTS.length
  return (
    <span
      className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-[9px] text-[13px] font-semibold', AVATAR_TINTS[index])}
      aria-hidden="true"
    >
      {name.slice(0, 2).toUpperCase()}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Status filter tabs: a horizontally scrolling row of pills with counts.
// ---------------------------------------------------------------------------
interface TabsProps {
  active: string
  stats: ApplicationStats | undefined
  onChange: (value: string) => void
}

export function StatusTabs({ active, stats, onChange }: TabsProps) {
  const tabs = [
    { value: 'all', label: 'All', count: stats?.total ?? 0 },
    { value: 'submitted', label: 'Submitted', count: stats?.submitted ?? 0 },
    { value: 'in_flight', label: 'Applying', count: (stats?.inFlight ?? 0) + (stats?.queued ?? 0) },
    { value: 'needs_you', label: 'Needs you', count: stats?.needsYou ?? 0 },
    { value: 'failed', label: 'Failed', count: stats?.failed ?? 0 },
    { value: 'skipped', label: 'Skipped', count: stats?.skipped ?? 0 },
  ]

  return (
    <div role="tablist" aria-label="Application status" className="hscroll -mx-1 gap-1 px-1 py-0.5">
      {tabs.map((tab) => {
        const selected = tab.value === active
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.value)}
            className={cn(
              'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[9px] px-3 text-[13px] font-medium transition-colors',
              selected ? 'bg-ink text-oncolor' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
            )}
          >
            {tab.label}
            <span
              className={cn(
                'num min-w-[18px] rounded-pill px-1.5 text-center text-[11px] leading-[18px]',
                selected ? 'bg-oncolor/15 text-oncolor' : 'bg-ink/[0.05] text-ink-muted',
              )}
            >
              {tab.count}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The at-a-glance strip shared by the dashboard and tracker. Each segment is a
// shortcut into the matching status filter; tapping the active one clears it.
// ---------------------------------------------------------------------------
export function ApplicationStatTiles({
  stats,
  active,
  onChange,
}: {
  stats: ApplicationStats | undefined
  active: string
  onChange: (value: string) => void
}) {
  const inFlight = (stats?.inFlight ?? 0) + (stats?.queued ?? 0)
  const needsYou = stats?.needsYou ?? 0
  const failed = stats?.failed ?? 0

  const segments: StatSegment[] = [
    { key: 'submitted', label: 'Submitted', value: stats?.submitted ?? 0, tone: 'success', hint: 'Sent to the employer' },
    { key: 'in_flight', label: 'Applying', value: inFlight, tone: 'info', live: inFlight > 0, hint: inFlight > 0 ? 'Running right now' : 'Nothing queued' },
    { key: 'needs_you', label: 'Needs you', value: needsYou, tone: needsYou > 0 ? 'warning' : 'neutral', hint: needsYou > 0 ? 'Questions, or a run to review' : 'All clear' },
    { key: 'failed', label: 'Failed', value: failed, tone: failed > 0 ? 'danger' : 'neutral', hint: failed > 0 ? 'Worth a retry' : 'No failures' },
  ]

  return (
    <StatStrip
      segments={segments}
      active={active}
      loading={!stats}
      onSelect={(key) => onChange(active === key ? 'all' : key)}
    />
  )
}

/** Tab value → API status filter. "Applying" covers queued work too. */
export function statusesFor(tab: string): string[] | undefined {
  if (tab === 'all') return undefined
  if (tab === 'in_flight') return ['queued', 'in_flight']
  return [tab]
}
